import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Send, Loader2, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackContactForm } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = ({ onClose, title = "Napisz do nas" }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Wypełnij wszystkie pola');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });

      if (res.ok) {
        setSent(true);
        
        // Śledź konwersję formularza kontaktowego w Google Ads
        trackContactForm();
        
        toast.success('Wiadomość wysłana!');
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Nie udało się wysłać wiadomości');
      }
    } catch (error) {
      toast.error('Błąd wysyłania wiadomości');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Dziękujemy!</h3>
          <p className="text-slate-600">Twoja wiadomość została wysłana. Odpowiemy najszybciej jak to możliwe.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">{title}</CardTitle>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="contact-name">Imię i nazwisko</Label>
            <Input 
              id="contact-name" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="Jan Kowalski"
              data-testid="contact-name"
            />
          </div>
          <div>
            <Label htmlFor="contact-email">Email</Label>
            <Input 
              id="contact-email" 
              type="email"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="jan@email.pl"
              data-testid="contact-email"
            />
          </div>
          <div>
            <Label htmlFor="contact-message">Wiadomość</Label>
            <Textarea 
              id="contact-message" 
              value={message} 
              onChange={(e) => setMessage(e.target.value)} 
              placeholder="Twoja wiadomość..."
              rows={4}
              data-testid="contact-message"
            />
          </div>
          <Button 
            type="submit" 
            disabled={sending} 
            className="w-full bg-orange-500 hover:bg-orange-600"
            data-testid="contact-submit"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wysyłanie...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" />Wyślij wiadomość</>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
